import { Link } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import type { DealOfferingSummary, DealOfferingStatus } from '@/types/deal_offering'

interface Props {
  offerings: DealOfferingSummary[]
}

const STATUS_BADGES: Record<DealOfferingStatus, { label: string; bg: string; text: string }> = {
  draft: { label: 'Draft', bg: 'bg-gray-100 border-gray-200', text: 'text-gray-600' },
  open: { label: 'Open', bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700' },
  locked: { label: 'Locked', bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700' },
  funded: { label: 'Funded', bg: 'bg-indigo-50 border-indigo-200', text: 'text-indigo-700' },
  cancelled: { label: 'Cancelled', bg: 'bg-red-50 border-red-200', text: 'text-red-700' },
}

function formatCurrency(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

export default function DealOfferingsIndex({ offerings }: Props) {
  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Deal Offerings</h1>
          <p className="text-sm text-gray-500 mt-1">Post deals to investors and collect commitments</p>
        </div>
        <Link
          href="/deal_offerings/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          New Offering
        </Link>
      </div>

      {offerings.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
            </svg>
          </div>
          <p className="text-sm text-gray-500 mb-4">No deal offerings yet. Create one from a loan page or here.</p>
          <Link
            href="/deal_offerings/new"
            className="text-sm font-semibold text-emerald-600 hover:text-emerald-700"
          >
            Create your first offering
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {offerings.map((o) => {
            const badge = STATUS_BADGES[o.status] || STATUS_BADGES.draft
            return (
              <Link
                key={o.id}
                href={`/deal_offerings/${o.id}`}
                className="bg-white rounded-xl border border-gray-200 p-5 hover:border-gray-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{o.borrower_name}</p>
                    <p className="text-xs text-gray-400">{o.loan_number}</p>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full border ${badge.bg} ${badge.text}`}>
                    {badge.label}
                  </span>
                </div>

                <p className="text-lg font-bold text-gray-900 mb-3">${formatCurrency(o.total_amount)}</p>

                {/* Progress bar */}
                <div className="mb-2">
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(o.percent_funded, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[11px] text-gray-400 mt-1">
                    <span>${formatCurrency(o.committed_amount)} committed</span>
                    <span>{o.percent_funded}%</span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-100">
                  <span>{o.invitation_count} investor{o.invitation_count !== 1 ? 's' : ''} invited</span>
                  <span>{new Date(o.created_at).toLocaleDateString()}</span>
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </AppLayout>
  )
}
